/**
 * PhysicsPanel - Physics simulation controls
 * Play/Pause, Reset, Figure visibility, Stability testing.
 * Vertical layout inside the right properties panel; buttons use inline
 * Phosphor SVG icons (the Play button swaps to a Pause icon while running).
 */

import { ICONS, phIcon } from '../icons.js';

export class PhysicsPanel {
    constructor(application) {
        this.app = application;
        this.isPlaying = false;
        this.createPanel();
    }

    /**
     * Create the panel DOM structure
     */
    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'physics-panel panel';
        panel.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 100%;
        `;

        // Simulation controls section
        const simControls = this.createSection('Simulation', [
            { icon: ICONS.play, label: 'Play', tooltip: 'Play physics simulation', action: () => this.togglePlayPause() },
            { icon: ICONS.arrowCounterClockwise, label: 'Reset', tooltip: 'Reset simulation', action: () => this.reset() },
            { icon: ICONS.personArmsSpread, label: 'Test', tooltip: 'Test chair stability', action: () => this.testStability() }
        ]);
        panel.appendChild(simControls);

        // Figure controls: show/hide, sit/stand, and a single size slot
        panel.appendChild(this.createFigureSection());

        // NOTE: the collider-hull debug draw (PhysicsDebugRenderer) is
        // developer tooling, toggled with the 'h' key only - deliberately
        // no visible button here.

        // Status display
        const statusSection = document.createElement('div');
        statusSection.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 4px;
        `;

        const statusTitle = document.createElement('div');
        statusTitle.textContent = 'Status';
        statusTitle.className = 'props-field-label';
        statusSection.appendChild(statusTitle);

        this.statusDisplay = document.createElement('div');
        this.statusDisplay.style.cssText = `
            color: #666;
            font-size: 12px;
            padding: 8px;
            background: rgba(0, 0, 0, 0.05);
            border-radius: 4px;
        `;
        this.statusDisplay.innerHTML = `
            <div>Physics: <span style="color: var(--accent-strong); font-weight: 600;">Ready</span></div>
            <div>Figure: <span style="color: #888;">Hidden</span></div>
        `;
        statusSection.appendChild(this.statusDisplay);

        panel.appendChild(statusSection);

        this.panel = panel;
        this.playButton = simControls.querySelector('[data-label="Play"]');
    }

    /**
     * Figure row: [Show/Hide] [Sit/Stand] [Size − +]. The bigger/smaller
     * buttons share ONE slot so the three controls line up evenly.
     */
    createFigureSection() {
        const section = document.createElement('div');
        section.className = 'panel-section';
        section.style.cssText = 'display: flex; flex-direction: column; gap: 4px;';

        const titleEl = document.createElement('div');
        titleEl.textContent = 'Figure';
        titleEl.className = 'props-field-label';
        section.appendChild(titleEl);

        const row = document.createElement('div');
        row.style.cssText = 'display: flex; gap: 4px;';

        row.appendChild(this.createToolButton({
            icon: ICONS.person, label: 'Toggle',
            tooltip: 'Show/Hide ergonomic figure', action: () => this.toggleFigure()
        }));

        this.sitButton = this.createToolButton({
            icon: ICONS.officeChair, label: 'Sit',
            tooltip: 'Sit / stand the figure', action: () => this.togglePose()
        });
        row.appendChild(this.sitButton);

        row.appendChild(this.createSizeControl());

        section.appendChild(row);
        return section;
    }

    /**
     * Bigger/smaller as a single slot: a − / + pair over a "Size" label,
     * matching the column shape of the other tool buttons.
     */
    createSizeControl() {
        const slot = document.createElement('div');
        slot.style.cssText = `
            flex: 1; display: flex; flex-direction: column; align-items: center;
            gap: 4px; padding: 6px 4px; border: 2px solid transparent;
            border-radius: 8px;`;

        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'display: flex; gap: 6px; align-items: center; justify-content: center; height: 20px;';

        const mk = (icon, title, action) => {
            const b = document.createElement('button');
            b.title = title;
            b.style.cssText = `
                background: transparent; border: none; padding: 2px; cursor: pointer;
                color: #444; display: flex; align-items: center; justify-content: center;
                border-radius: 4px;`;
            b.appendChild(phIcon(icon, 16));
            b.addEventListener('mouseenter', () => { b.style.background = 'rgba(200, 200, 200, 0.4)'; });
            b.addEventListener('mouseleave', () => { b.style.background = 'transparent'; });
            b.addEventListener('click', action);
            return b;
        };
        btnRow.appendChild(mk(ICONS.minus, 'Smaller', () => this.scaleFigure(0.9)));
        btnRow.appendChild(mk(ICONS.plus, 'Bigger', () => this.scaleFigure(1.1)));
        slot.appendChild(btnRow);

        const label = document.createElement('div');
        label.textContent = 'Size';
        label.style.cssText = 'color: #666; font-size: 10px; text-align: center;';
        slot.appendChild(label);
        return slot;
    }

    /**
     * Create a section with tools
     */
    createSection(title, tools) {
        const section = document.createElement('div');
        section.className = 'panel-section';
        section.style.cssText = `
            display: flex;
            flex-direction: column;
            gap: 4px;
        `;

        // Section title
        const titleEl = document.createElement('div');
        titleEl.textContent = title;
        titleEl.className = 'props-field-label';
        section.appendChild(titleEl);

        // Tools row
        const toolsRow = document.createElement('div');
        toolsRow.style.cssText = `
            display: flex;
            gap: 4px;
        `;

        tools.forEach(tool => {
            const button = this.createToolButton(tool);
            toolsRow.appendChild(button);
        });

        section.appendChild(toolsRow);
        return section;
    }

    /**
     * Create a tool button (inline Phosphor SVG icon + label)
     */
    createToolButton(tool) {
        const button = document.createElement('button');
        button.title = tool.tooltip;
        button.dataset.label = tool.label;
        button.className = 'sc-tool-btn';
        button.style.cssText = `
            background: transparent;
            border: 2px solid transparent;
            border-radius: 8px;
            padding: 6px 8px;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            flex: 1;
            color: #444;
        `;

        // Icon holder (content swapped for the Play/Pause toggle)
        const iconHolder = document.createElement('span');
        iconHolder.style.cssText = `
            display: flex;
            align-items: center;
            justify-content: center;
            height: 20px;
        `;
        iconHolder.appendChild(phIcon(tool.icon, 20));
        button.appendChild(iconHolder);

        // Label
        const label = document.createElement('div');
        label.textContent = tool.label;
        label.style.cssText = `
            color: #666;
            font-size: 10px;
            text-align: center;
        `;
        button.appendChild(label);

        // Hover
        button.addEventListener('mouseenter', () => {
            button.style.background = 'rgba(200, 200, 200, 0.3)';
            label.style.color = '#000';
        });

        button.addEventListener('mouseleave', () => {
            button.style.background = 'transparent';
            label.style.color = '#666';
        });

        // Click handler
        button.addEventListener('click', tool.action);

        // Kept for the play/pause icon+label swap
        button._iconHolder = iconHolder;
        button._labelEl = label;

        return button;
    }

    /**
     * Swap the Play button between the play and pause states
     */
    setPlayButtonState(playing) {
        const holder = this.playButton?._iconHolder;
        const label = this.playButton?._labelEl;
        if (holder) holder.replaceChildren(phIcon(playing ? ICONS.pause : ICONS.play, 20));
        if (label) label.textContent = playing ? 'Pause' : 'Play';
    }

    /**
     * Toggle play/pause
     */
    togglePlayPause() {
        this.isPlaying = !this.isPlaying;

        if (this.app.togglePhysics) {
            this.app.togglePhysics(this.isPlaying);
        }

        this.setPlayButtonState(this.isPlaying);
        this.updateStatus(this.isPlaying ? 'Simulation: Running' : 'Simulation: Paused');
    }

    /**
     * Reset physics simulation
     */
    reset() {
        if (this.app.resetPhysics) {
            this.app.resetPhysics();
        }

        this.isPlaying = false;
        this.setPlayButtonState(false);
        this.updateStatus('Simulation: Reset');
    }

    /**
     * Toggle figure visibility
     */
    toggleFigure() {
        if (this.app.toggleRagdoll) {
            const visible = this.app.toggleRagdoll();
            this.updateFigureStatus(visible ? 'Visible' : 'Hidden');
        }
    }

    /**
     * Scale figure (status line shows the resulting height + age)
     */
    scaleFigure(factor) {
        if (this.app.scaleRagdoll) {
            this.app.scaleRagdoll(factor);
            this.syncFigureState();
            this.app.updateFigureWidget?.();
        }
    }

    /**
     * Toggle the figure between standing and seated (re-posed in place).
     */
    togglePose() {
        if (!this.app.toggleFigurePose) return;
        this.app.toggleFigurePose();
        this.syncFigureState();
        this.app.updateFigureWidget?.();
    }

    /**
     * Reconcile the Figure status line and the Sit/Stand button with the
     * figure's live state. Called after any change (from this panel OR the
     * on-canvas pill), so the two controls never drift apart.
     */
    syncFigureState() {
        const doll = this.app.ragdoll;
        if (doll?.visible) this.updateFigureStatus('Visible');
        const seated = doll?.currentPose === 'seated';
        if (this.sitButton?._labelEl) {
            this.sitButton._labelEl.textContent = seated ? 'Stand' : 'Sit';
        }
        if (this.sitButton?._iconHolder) {
            this.sitButton._iconHolder.replaceChildren(
                phIcon(seated ? ICONS.person : ICONS.officeChair, 20));
        }
    }

    /**
     * Test chair stability
     */
    async testStability() {
        if (this.app.testChairStability) {
            this.updateStatus('Testing stability...');

            try {
                const results = await this.app.testChairStability(3.0);

                if (results.stable) {
                    this.updateStatus('Stable ✓');
                    console.log('✓ Chair is stable!', results);
                } else {
                    this.updateStatus('Unstable ✗');
                    console.log('✗ Chair is unstable', results);
                }
            } catch (error) {
                this.updateStatus('Test failed');
                console.error('Stability test error:', error);
            }
        }
    }

    /**
     * Update status display
     */
    updateStatus(message) {
        const statusLines = this.statusDisplay.querySelectorAll('div');
        if (statusLines[0]) {
            statusLines[0].innerHTML = `Physics: <span style="color: var(--accent-strong); font-weight: 600;">${message.split(': ')[1] || 'Ready'}</span>`;
        }
    }

    /**
     * Update figure status
     */
    updateFigureStatus(status) {
        const statusLines = this.statusDisplay.querySelectorAll('div');
        if (statusLines[1]) {
            const color = status === 'Visible' ? 'var(--accent-strong)' : '#888';
            // Visible figures also report their size as a person:
            // height + approximate age (from the age-proportion blend)
            let detail = '';
            const doll = this.app.ragdoll;
            if (status === 'Visible' && doll?.getAgeDescription) {
                const metres = (doll.getStandingHeight() / 10).toFixed(2);
                detail = ` <span style="color: #888;">· ${metres}m, ${doll.getAgeDescription()}</span>`;
            }
            statusLines[1].innerHTML = `Figure: <span style="color: ${color}; font-weight: 600;">${status}</span>${detail}`;
        }
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Update panel state
     */
    update() {
        // Update status if needed
    }
}
